import React, { useEffect, useState } from 'react';
import { Bell, Save, AlertCircle, Send, CheckCircle, MessageSquare, Flame, Sparkles, Plus, Trash2, Users, Link2, Copy, Loader2, ExternalLink, Phone, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENTS = [
  {
    key: 'new_lead',
    label: 'New lead arrives',
    desc: 'Contact form, deal enquiry, or popup sign-up creates a new CRM lead.',
    icon: <Sparkles className="w-4 h-4 text-[#D4AF37]" />,
  },
  {
    key: 'whatsapp_inbound',
    label: 'Inbound WhatsApp from a lead',
    desc: 'A lead replies on WhatsApp (sandbox or business number).',
    icon: <MessageSquare className="w-4 h-4 text-[#25D366]" />,
  },
  {
    key: 'hot_lead_reminder',
    label: 'Daily hot-lead reminder',
    desc: 'Once a day, push a digest of any hot leads still in "New" stage >24h.',
    icon: <Flame className="w-4 h-4 text-orange-400" />,
  },
  {
    key: 'bespoke_stage_change',
    label: 'Bespoke brief enters a new stage',
    desc: 'Push when a bespoke brief is moved to Contacted, Brief Confirmed, Sourcing, Deal Presented, Won, Lost, or Refunded.',
    icon: <Sparkles className="w-4 h-4 text-cyan-400" />,
  },
  {
    key: 'bespoke_deposit_paid',
    label: 'Bespoke deposit paid',
    desc: 'Dedicated 💎 alert with the deadline when an investor pays their refundable holding deposit.',
    icon: <Sparkles className="w-4 h-4 text-[#D4AF37]" />,
  },
  {
    key: 'bespoke_deadline',
    label: 'Bespoke sourcing deadline approaching',
    desc: 'Auto-reminder at 7 days, 1 day, and overdue for any paid brief that hasn\'t closed yet.',
    icon: <Flame className="w-4 h-4 text-red-400" />,
  },
];

export const NotificationsTab = ({ getStoredPassword }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // recipient id being tested, or 'all'
  const [newLabel, setNewLabel] = useState('');
  const [newChatId, setNewChatId] = useState('');
  const [adding, setAdding] = useState(false);
  // Invite flow
  const [inviteLabel, setInviteLabel] = useState('');
  const [inviting, setInviting] = useState(false);
  const [mode, setMode] = useState('invite'); // 'invite' = generate link | 'manual' = paste chat ID
  // WhatsApp admin recipients
  const [waLabel, setWaLabel] = useState('');
  const [waPhone, setWaPhone] = useState('');
  const [waAdding, setWaAdding] = useState(false);
  const [waTesting, setWaTesting] = useState(null); // phone being tested, or 'all'

  // Email admin recipients
  const [emailLabel, setEmailLabel] = useState('');
  const [emailAddr,  setEmailAddr]  = useState('');
  const [emailAdding, setEmailAdding] = useState(false);
  const [emailTesting, setEmailTesting] = useState(null); // email being tested, or 'all'

  // Drip nudge scheduler
  const [drip, setDrip] = useState(null);
  const [dripSaving, setDripSaving] = useState(false);
  const [dripRunning, setDripRunning] = useState(false);

  const loadDrip = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/drip-settings?password=${getStoredPassword()}`);
      if (r.ok) setDrip(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { loadDrip(); /* eslint-disable-next-line */ }, []);

  const saveDrip = async (patch) => {
    setDripSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/drip-settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        setDrip(await r.json());
        toast.success('Drip settings saved');
      } else {
        toast.error('Failed to save drip settings');
      }
    } catch { toast.error('Network error'); }
    setDripSaving(false);
  };

  const runDripNow = async () => {
    setDripRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/run-drip-now?password=${getStoredPassword()}`, { method: 'POST' });
      const d = await r.json();
      if (!d.ran) {
        toast.info('Drip is currently disabled — turn it on to send nudges');
      } else {
        const { eligible = 0, sent = 0 } = d;
        if (eligible === 0) toast.info('No stale briefs need a nudge right now');
        else toast.success(`Drip pushed — ${sent}/${eligible} brief${eligible !== 1 ? 's' : ''} nudged`);
      }
      await loadDrip();
    } catch { toast.error('Network error'); }
    setDripRunning(false);
  };

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/settings?password=${getStoredPassword()}`);
      if (r.ok) setSettings(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-poll every 5s while on the tab so newly-registered invite recipients
  // appear without a manual refresh.  Stops polling on unmount.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telegram_enabled: settings.telegram_enabled,
          whatsapp_enabled: settings.whatsapp_enabled,
          email_enabled:    settings.email_enabled,
          events: settings.events,
        }),
      });
      if (res.ok) toast.success('Notification rules saved');
      else toast.error('Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const sendTest = async (chatId) => {
    setTesting(chatId || 'all');
    try {
      const qs = chatId ? `&chat_id=${encodeURIComponent(chatId)}` : '';
      const r = await fetch(`${API_URL}/api/admin/notifications/test?password=${getStoredPassword()}${qs}`, { method: 'POST' });
      const d = await r.json();
      if (d.success) toast.success(d.message);
      else toast.error(d.message, { duration: 8000 });
    } catch { toast.error('Network error'); }
    setTesting(null);
  };

  const triggerDigest = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/run-hot-reminder?password=${getStoredPassword()}`, { method: 'POST' });
      const d = await r.json();
      const count = d.result?.count || 0;
      if (count > 0) toast.success(`Digest pushed — ${count} hot lead${count !== 1 ? 's' : ''}`);
      else toast.info('No hot leads stuck >24h right now');
    } catch { toast.error('Network error'); }
  };

  const addRecipient = async () => {
    if (!newLabel.trim() || !newChatId.trim()) {
      toast.error('Both label and chat ID are required');
      return;
    }
    setAdding(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/recipients?password=${getStoredPassword()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newLabel.trim(), chat_id: newChatId.trim(), enabled: true }),
      });
      if (r.ok) {
        toast.success(`Added ${newLabel.trim()}`);
        setNewLabel('');
        setNewChatId('');
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not add recipient');
      }
    } catch { toast.error('Network error'); }
    setAdding(false);
  };

  const createInvite = async () => {
    if (!inviteLabel.trim()) {
      toast.error('Enter a label first (e.g. "Op Meng")');
      return;
    }
    setInviting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/recipients/invite?password=${getStoredPassword()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: inviteLabel.trim() }),
      });
      if (r.ok) {
        const data = await r.json();
        try {
          await navigator.clipboard.writeText(data.invite_url);
          toast.success('Invite link copied — send it to your team member on WhatsApp / Email / SMS.', { duration: 6000 });
        } catch {
          toast.success('Invite created. Copy the link from the pending recipient below.');
        }
        setInviteLabel('');
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not create invite');
      }
    } catch { toast.error('Network error'); }
    setInviting(false);
  };

  const copyInviteLink = async (recipient) => {
    if (!settings?.bot_username || !recipient.pending_token) return;
    const url = `https://t.me/${settings.bot_username}?start=${recipient.pending_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch {
      toast.info(url, { duration: 8000 });
    }
  };

  const toggleRecipient = async (recipient) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/recipients/${recipient.id}?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !recipient.enabled }),
      });
      if (r.ok) await load();
      else toast.error('Update failed');
    } catch { toast.error('Network error'); }
  };

  const deleteRecipient = async (recipient) => {
    if (!window.confirm(`Remove ${recipient.label} from notifications?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/recipients/${recipient.id}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (r.ok) {
        toast.success('Recipient removed');
        await load();
      } else toast.error('Delete failed');
    } catch { toast.error('Network error'); }
  };

  // ---------- WhatsApp admin recipients ----------
  const addWhatsAppRecipient = async () => {
    if (!waLabel.trim() || !waPhone.trim()) {
      toast.error('Both name and phone are required');
      return;
    }
    setWaAdding(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/whatsapp-recipients?password=${getStoredPassword()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: waLabel.trim(), phone: waPhone.trim(), enabled: true }),
      });
      if (r.ok) {
        toast.success(`Added ${waLabel.trim()}`);
        setWaLabel('');
        setWaPhone('');
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not add recipient');
      }
    } catch { toast.error('Network error'); }
    setWaAdding(false);
  };

  const toggleWhatsAppRecipient = async (recipient) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/whatsapp-recipients/${recipient.id}?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !recipient.enabled }),
      });
      if (r.ok) await load();
      else toast.error('Update failed');
    } catch { toast.error('Network error'); }
  };

  const deleteWhatsAppRecipient = async (recipient) => {
    if (!window.confirm(`Remove ${recipient.label} from WhatsApp alerts?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/whatsapp-recipients/${recipient.id}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (r.ok) {
        toast.success('Recipient removed');
        await load();
      } else toast.error('Delete failed');
    } catch { toast.error('Network error'); }
  };

  const sendWhatsAppTest = async (phone) => {
    setWaTesting(phone || 'all');
    try {
      const qs = phone ? `&phone=${encodeURIComponent(phone)}` : '';
      const r = await fetch(`${API_URL}/api/admin/notifications/whatsapp-test?password=${getStoredPassword()}${qs}`, { method: 'POST' });
      const d = await r.json();
      if (d.success) toast.success(d.message);
      else toast.error(d.message, { duration: 9000 });
    } catch { toast.error('Network error'); }
    setWaTesting(null);
  };

  const openWhatsAppInvite = (recipient) => {
    const joinCode = settings.whatsapp_sandbox_join_code || '<your-sandbox-code>';
    const from = (settings.whatsapp_from || '+14155238886').replace(/\s/g, '');
    const text =
      `Hi ${recipient.label.split(' ')[0]} —\n\n` +
      `To receive Black Star PI notifications on this WhatsApp, please opt-in once:\n\n` +
      `1. Save *${from}* as a contact (any name).\n` +
      `2. Open WhatsApp and message that contact:\n\n` +
      `   *join ${joinCode.replace(/^join\s+/i, '')}*\n\n` +
      `That's it — you'll be opted in. (Twilio sandbox; sessions refresh every 72 hours of activity.)\n\n` +
      `— Black Star PI`;
    const cleanPhone = recipient.phone.replace(/[^\d]/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  if (!settings) return <div className="text-white/50 text-sm">Loading…</div>;

  const setEvent = (key, val) => setSettings({ ...settings, events: { ...settings.events, [key]: val } });
  const recipients = settings.telegram_recipients || [];
  const waRecipients = settings.whatsapp_recipients || [];
  const emailRecipients = settings.email_recipients || [];

  // Email recipient handlers (mirror Telegram/WhatsApp shape)
  const addEmailRecipient = async () => {
    const label = emailLabel.trim();
    const email = emailAddr.trim();
    if (!label || !email) {
      toast.error('Both label and email are required');
      return;
    }
    setEmailAdding(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/email-recipients?password=${getStoredPassword()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, email, enabled: true }),
      });
      if (r.ok) {
        toast.success(`Added ${label}`);
        setEmailLabel(''); setEmailAddr('');
        await load();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to add recipient');
      }
    } catch { toast.error('Network error'); }
    setEmailAdding(false);
  };

  const toggleEmailRecipient = async (rec) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/email-recipients/${rec.id}?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !rec.enabled }),
      });
      if (r.ok) await load();
    } catch { toast.error('Network error'); }
  };

  const removeEmailRecipient = async (rec) => {
    if (!window.confirm(`Remove email recipient "${rec.label}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications/email-recipients/${rec.id}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (r.ok) {
        toast.success('Recipient removed');
        await load();
      }
    } catch { toast.error('Network error'); }
  };

  const sendEmailTest = async (addr) => {
    setEmailTesting(addr || 'all');
    try {
      const qs = addr ? `&email=${encodeURIComponent(addr)}` : '';
      const r = await fetch(`${API_URL}/api/admin/notifications/email-test?password=${getStoredPassword()}${qs}`, { method: 'POST' });
      const d = await r.json();
      if (d.success) toast.success(d.message);
      else toast.error(d.message || 'Send failed', { duration: 8000 });
    } catch { toast.error('Network error'); }
    setEmailTesting(null);
  };
  const hasAnyRecipient = recipients.length > 0 || settings.env_chat_id_set || waRecipients.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl text-white font-light flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#D4AF37]" /> Push Notifications
        </h2>
        <p className="text-white/50 text-sm mt-1">
          Real-time alerts on your phone via Telegram. Free, instant, no Twilio cost.
        </p>
      </div>

      {/* Setup status */}
      {!settings.bot_token_set ? (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-5 space-y-4" data-testid="telegram-setup-notice">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-yellow-100/90">
              <p className="font-semibold text-sm">Telegram bot token not yet configured</p>
              <p className="text-xs mt-0.5">Follow the steps below — takes ~2 minutes.</p>
            </div>
          </div>
          <ol className="text-white/70 text-xs space-y-2.5 pl-4 list-decimal">
            <li>
              In Telegram, open a chat with <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-semibold underline">@BotFather</a>. Send <code className="bg-black/40 px-1.5 py-0.5 rounded text-[#D4AF37]">/newbot</code> and follow the prompts.
            </li>
            <li>
              BotFather sends you a token like <code className="bg-black/40 px-1.5 py-0.5 rounded text-[#D4AF37] text-[11px] break-all">123456789:ABCdef...</code>. Add it to backend <code>.env</code> as <code className="bg-black/40 px-1 rounded">TELEGRAM_BOT_TOKEN</code>.
            </li>
            <li>
              Open your new bot in Telegram and click <strong>Start</strong>. Then add recipients below.
            </li>
          </ol>
        </div>
      ) : (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2.5">
          <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0 mt-0.5" />
          <p className="text-green-100/90 text-xs">
            <strong>Bot token is configured.</strong> Add or manage notification recipients below.
          </p>
        </div>
      )}

      {/* ========== RECIPIENTS ========== */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4" data-testid="telegram-recipients-section">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="text-white font-semibold text-sm">Recipients</h3>
          <span className="text-white/40 text-xs">({recipients.length})</span>
        </div>
        <p className="text-white/50 text-xs -mt-2">
          Each recipient gets every enabled notification on their Telegram. To find your Chat ID, message{' '}
          <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">@userinfobot</a> on Telegram.
        </p>

        {/* List */}
        {recipients.length === 0 ? (
          <p className="text-white/40 text-xs italic py-3 text-center bg-white/[0.02] rounded">
            No recipients added yet.{settings.env_chat_id_set ? ' Currently falling back to TELEGRAM_CHAT_ID from .env.' : ''}
          </p>
        ) : (
          <div className="space-y-2">
            {recipients.map(r => {
              const isPending = !!r.pending_token && !r.chat_id;
              return (
                <div
                  key={r.id}
                  className={`flex items-center gap-2.5 bg-[#0a1a14] border rounded-lg px-3 py-2.5 ${
                    isPending ? 'border-[#D4AF37]/30' : 'border-white/10'
                  }`}
                  data-testid={`recipient-${r.id}`}
                >
                  {isPending ? (
                    <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin flex-shrink-0" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={!!r.enabled}
                      onChange={() => toggleRecipient(r)}
                      className="accent-[#D4AF37] w-4 h-4 flex-shrink-0"
                      data-testid={`recipient-toggle-${r.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${
                      isPending ? 'text-[#D4AF37]' : (r.enabled ? 'text-white' : 'text-white/40 line-through')
                    }`}>{r.label}</p>
                    {isPending ? (
                      <p className="text-white/40 text-[11px] truncate italic">Waiting for them to tap the invite link…</p>
                    ) : (
                      <p className="text-white/40 text-[11px] truncate font-mono">{r.chat_id}</p>
                    )}
                  </div>
                  {isPending ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyInviteLink(r)}
                        className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2.5 text-xs"
                        data-testid={`recipient-copy-invite-${r.id}`}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                      </Button>
                      <a
                        href={settings.bot_username ? `https://t.me/${settings.bot_username}?start=${r.pending_token}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center h-8 px-2.5 text-xs rounded border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"
                        title="Open the invite in Telegram (for testing)"
                        data-testid={`recipient-open-invite-${r.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendTest(r.chat_id)}
                      disabled={testing === r.chat_id}
                      className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2.5 text-xs"
                      data-testid={`recipient-test-${r.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> {testing === r.chat_id ? '…' : 'Test'}
                    </Button>
                  )}
                  <button
                    onClick={() => deleteRecipient(r)}
                    className="text-white/40 hover:text-red-400 p-1.5 rounded transition-colors"
                    data-testid={`recipient-delete-${r.id}`}
                    aria-label={`Remove ${r.label}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add new — invite link (recommended) or manual chat ID */}
        <div className="border-t border-white/10 pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-white/60 text-[11px] uppercase tracking-widest font-semibold flex-1">Add Recipient</p>
            <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-[10px] font-semibold">
              <button
                onClick={() => setMode('invite')}
                className={`px-2.5 py-1.5 transition-colors ${mode === 'invite' ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                data-testid="recipient-mode-invite"
              >
                <Link2 className="w-3 h-3 inline mr-1" /> Invite Link
              </button>
              <button
                onClick={() => setMode('manual')}
                className={`px-2.5 py-1.5 transition-colors ${mode === 'manual' ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                data-testid="recipient-mode-manual"
              >
                Manual ID
              </button>
            </div>
          </div>

          {mode === 'invite' ? (
            <div className="space-y-2.5">
              <p className="text-white/50 text-xs leading-relaxed">
                Generate a one-tap onboarding link — your team member opens it on Telegram and clicks <strong>Start</strong>. We auto-register their chat ID. No `@userinfobot` lookup needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={inviteLabel}
                  onChange={e => setInviteLabel(e.target.value)}
                  placeholder="Name (e.g. Op Meng, Adam)"
                  onKeyDown={e => { if (e.key === 'Enter' && !inviting) createInvite(); }}
                  className="flex-1 bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="new-invite-label"
                />
                <Button
                  onClick={createInvite}
                  disabled={inviting || !inviteLabel.trim() || !settings.bot_token_set}
                  className="bg-[#D4AF37] text-black hover:bg-[#B8941E] whitespace-nowrap"
                  data-testid="create-invite-btn"
                >
                  <Link2 className="w-4 h-4 mr-1.5" />
                  {inviting ? 'Creating…' : 'Generate & Copy Invite'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              <p className="text-white/50 text-xs leading-relaxed">
                Already have the recipient's Telegram Chat ID? Paste it directly here.
                Get it via <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">@userinfobot</a> on Telegram.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  placeholder="Name (e.g. Adam, Sarah)"
                  className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="new-recipient-label"
                />
                <input
                  value={newChatId}
                  onChange={e => setNewChatId(e.target.value)}
                  placeholder="Chat ID (e.g. 987654321)"
                  className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 font-mono"
                  data-testid="new-recipient-chat-id"
                />
              </div>
              <Button
                onClick={addRecipient}
                disabled={adding || !newLabel.trim() || !newChatId.trim()}
                className="w-full sm:w-auto bg-[#D4AF37] text-black hover:bg-[#B8941E]"
                data-testid="add-recipient-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> {adding ? 'Adding…' : 'Add Recipient'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Master toggle */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="telegram-master-toggle">
          <input
            type="checkbox"
            checked={settings.telegram_enabled}
            onChange={e => setSettings({ ...settings, telegram_enabled: e.target.checked })}
            className="mt-1 accent-[#D4AF37] w-4 h-4"
          />
          <div>
            <p className="text-white font-semibold text-sm">Enable Telegram push notifications</p>
            <p className="text-white/50 text-xs mt-0.5">Master switch. Disables all rules below if off.</p>
          </div>
        </label>
      </div>

      {/* ========== WHATSAPP ADMIN PUSH ========== */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4" data-testid="whatsapp-recipients-section">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#25D366]" />
          <h3 className="text-white font-semibold text-sm">WhatsApp Admin Alerts</h3>
          <span className="text-white/40 text-xs">({waRecipients.length})</span>
        </div>
        <p className="text-white/50 text-xs -mt-2">
          Get the same admin alerts on WhatsApp as your team gets on Telegram — perfect for the road. Sent via Twilio.
          {settings.whatsapp_from && (
            <> Outbound number: <span className="text-white/70 font-mono">{settings.whatsapp_from}</span>.</>
          )}
        </p>

        {/* Sandbox notice — sleek setup card */}
        {settings.whatsapp_is_sandbox && (
          <div className="bg-gradient-to-br from-[#0a1a14] via-[#0a1a14] to-yellow-500/5 border border-yellow-500/30 rounded-lg p-4 space-y-3" data-testid="whatsapp-sandbox-notice">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-100 text-xs font-semibold uppercase tracking-widest">Twilio Sandbox — One-Time Setup</p>
                <p className="text-yellow-100/70 text-[11px] mt-1 leading-relaxed">
                  Each recipient must opt-in once by sending the join phrase below to <span className="font-mono text-yellow-100">{settings.whatsapp_from}</span> on WhatsApp. Sandbox sessions expire after ~72h of inactivity — if a test reports "session expired", just re-send the phrase.
                </p>
              </div>
            </div>

            {/* Join-phrase tile */}
            {settings.whatsapp_sandbox_join_code ? (
              <div className="bg-black/40 border border-yellow-500/40 rounded-md px-3 py-2.5 flex items-center justify-between gap-2" data-testid="whatsapp-join-code-tile">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-yellow-200/70 font-semibold">Your sandbox join phrase</p>
                  <p className="font-mono text-[#D4AF37] font-bold text-sm truncate">
                    join {settings.whatsapp_sandbox_join_code.replace(/^join\s+/i, '')}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const code = `join ${settings.whatsapp_sandbox_join_code.replace(/^join\s+/i, '')}`;
                    navigator.clipboard.writeText(code);
                    toast.success('Copied — share this with each recipient');
                  }}
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2.5 text-xs"
                  data-testid="copy-join-phrase-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                </Button>
              </div>
            ) : (
              <div className="bg-black/30 border border-yellow-500/20 border-dashed rounded-md p-3 text-[11px] text-yellow-100/80 leading-relaxed">
                <p className="font-semibold mb-1">To surface your sandbox join phrase inline:</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Open <a className="text-[#D4AF37] underline" href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noopener noreferrer">console.twilio.com → Messaging → Try WhatsApp <ExternalLink className="w-3 h-3 inline -mt-0.5" /></a></li>
                  <li>Click the <strong className="text-yellow-100">"Connect to sandbox"</strong> step (the first circle at the top)</li>
                  <li>Copy the two-word join phrase shown (e.g. <code className="bg-black/40 px-1 rounded text-[#D4AF37]">silver-dolphin</code>)</li>
                  <li>Add <code className="bg-black/40 px-1 rounded text-[#D4AF37]">TWILIO_WHATSAPP_SANDBOX_JOIN_CODE=silver-dolphin</code> to <code>/app/backend/.env</code></li>
                </ol>
              </div>
            )}
          </div>
        )}
        {!settings.twilio_set && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-100/90 text-[11px]">Twilio is not configured. Add <code className="bg-black/40 px-1 rounded">TWILIO_ACCOUNT_SID</code> and <code className="bg-black/40 px-1 rounded">TWILIO_AUTH_TOKEN</code> to backend <code>.env</code>.</p>
          </div>
        )}

        {/* Recipients list */}
        {waRecipients.length === 0 ? (
          <p className="text-white/40 text-xs italic py-3 text-center bg-white/[0.02] rounded">No WhatsApp recipients added yet.</p>
        ) : (
          <div className="space-y-2">
            {waRecipients.map(r => (
              <div
                key={r.id}
                className="flex items-center gap-2.5 bg-[#0a1a14] border border-white/10 rounded-lg px-3 py-2.5"
                data-testid={`wa-recipient-${r.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!r.enabled}
                  onChange={() => toggleWhatsAppRecipient(r)}
                  className="accent-[#25D366] w-4 h-4 flex-shrink-0"
                  data-testid={`wa-recipient-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${r.enabled ? 'text-white' : 'text-white/40 line-through'}`}>{r.label}</p>
                  <p className="text-white/40 text-[11px] truncate font-mono">{r.phone}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openWhatsAppInvite(r)}
                  className="border-white/15 text-white/70 hover:bg-white/5 h-8 px-2.5 text-xs"
                  data-testid={`wa-recipient-invite-${r.id}`}
                  title="Open WhatsApp pre-filled with the join instructions"
                >
                  <Share2 className="w-3.5 h-3.5 mr-1" /> Invite
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => sendWhatsAppTest(r.phone)}
                  disabled={waTesting === r.phone}
                  className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 h-8 px-2.5 text-xs"
                  data-testid={`wa-recipient-test-${r.id}`}
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> {waTesting === r.phone ? '…' : 'Test'}
                </Button>
                <button
                  onClick={() => deleteWhatsAppRecipient(r)}
                  className="text-white/40 hover:text-red-400 p-1.5 rounded transition-colors"
                  data-testid={`wa-recipient-delete-${r.id}`}
                  aria-label={`Remove ${r.label}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new */}
        <div className="border-t border-white/10 pt-4 space-y-2.5">
          <p className="text-white/60 text-[11px] uppercase tracking-widest font-semibold">Add Recipient</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={waLabel}
              onChange={e => setWaLabel(e.target.value)}
              placeholder="Name (e.g. Daniel)"
              className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#25D366]/40"
              data-testid="new-wa-recipient-label"
            />
            <div className="relative">
              <Phone className="w-3.5 h-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={waPhone}
                onChange={e => setWaPhone(e.target.value)}
                placeholder="+44 7759 342229"
                onKeyDown={e => { if (e.key === 'Enter' && !waAdding) addWhatsAppRecipient(); }}
                className="w-full bg-[#0a1a14] border border-white/10 rounded pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#25D366]/40 font-mono"
                data-testid="new-wa-recipient-phone"
              />
            </div>
          </div>
          <Button
            onClick={addWhatsAppRecipient}
            disabled={waAdding || !waLabel.trim() || !waPhone.trim() || !settings.twilio_set}
            className="w-full sm:w-auto bg-[#25D366] text-black hover:bg-[#1ea855]"
            data-testid="add-wa-recipient-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {waAdding ? 'Adding…' : 'Add Recipient'}
          </Button>
        </div>

        {/* Master toggle */}
        <div className="border-t border-white/10 pt-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="whatsapp-master-toggle">
            <input
              type="checkbox"
              checked={!!settings.whatsapp_enabled}
              onChange={e => setSettings({ ...settings, whatsapp_enabled: e.target.checked })}
              className="mt-1 accent-[#25D366] w-4 h-4"
            />
            <div>
              <p className="text-white font-semibold text-sm">Enable WhatsApp admin push notifications</p>
              <p className="text-white/50 text-xs mt-0.5">Master switch. When on, every admin event below also pings WhatsApp recipients.</p>
            </div>
          </label>
        </div>
      </div>

      {/* ========== EMAIL ADMIN RECIPIENTS ========== */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4" data-testid="email-recipients-section">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="text-white font-semibold text-sm">Email Recipients</h3>
          <span className="text-white/40 text-xs">({emailRecipients.length})</span>
        </div>
        <p className="text-white/50 text-xs -mt-2 leading-relaxed">
          Send every enabled admin event as a sleek branded email — mirrors what
          Telegram and WhatsApp recipients receive. Investor-facing emails
          (auto-replies, journey-deck attachments, signing links) are unchanged.
        </p>

        {/* List */}
        {emailRecipients.length === 0 ? (
          <p className="text-white/40 text-xs italic py-3 text-center bg-white/[0.02] rounded">
            No email recipients added yet.
          </p>
        ) : (
          <div className="space-y-2">
            {emailRecipients.map(r => (
              <div
                key={r.id}
                className="flex items-center gap-2.5 bg-[#0a1a14] border border-white/10 rounded-lg px-3 py-2.5"
                data-testid={`email-recipient-${r.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!r.enabled}
                  onChange={() => toggleEmailRecipient(r)}
                  className="accent-[#D4AF37] w-4 h-4 flex-shrink-0"
                  data-testid={`email-recipient-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${r.enabled ? 'text-white' : 'text-white/40 line-through'}`}>{r.label}</p>
                  <p className="text-white/40 text-[11px] truncate font-mono">{r.email}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => sendEmailTest(r.email)}
                  disabled={emailTesting === r.email}
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2.5 text-xs"
                  data-testid={`email-recipient-test-${r.id}`}
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> {emailTesting === r.email ? '…' : 'Test'}
                </Button>
                <button
                  onClick={() => removeEmailRecipient(r)}
                  className="text-white/40 hover:text-red-400 p-1.5 rounded transition-colors"
                  data-testid={`email-recipient-delete-${r.id}`}
                  aria-label={`Remove ${r.label}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new */}
        <div className="border-t border-white/10 pt-4 space-y-2.5">
          <p className="text-white/60 text-[11px] uppercase tracking-widest font-semibold">Add Recipient</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={emailLabel}
              onChange={e => setEmailLabel(e.target.value)}
              placeholder="Name (e.g. Daniel)"
              className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="new-email-recipient-label"
            />
            <input
              type="email"
              value={emailAddr}
              onChange={e => setEmailAddr(e.target.value)}
              placeholder="name@black-starpi.com"
              onKeyDown={e => { if (e.key === 'Enter' && !emailAdding) addEmailRecipient(); }}
              className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 font-mono"
              data-testid="new-email-recipient-addr"
            />
          </div>
          <Button
            onClick={addEmailRecipient}
            disabled={emailAdding || !emailLabel.trim() || !emailAddr.trim()}
            className="w-full sm:w-auto bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            data-testid="add-email-recipient-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {emailAdding ? 'Adding…' : 'Add Recipient'}
          </Button>
        </div>

        {/* Master toggle */}
        <div className="border-t border-white/10 pt-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="email-master-toggle">
            <input
              type="checkbox"
              checked={!!settings.email_enabled}
              onChange={e => setSettings({ ...settings, email_enabled: e.target.checked })}
              className="mt-1 accent-[#D4AF37] w-4 h-4"
            />
            <div>
              <p className="text-white font-semibold text-sm">Enable Email admin push notifications</p>
              <p className="text-white/50 text-xs mt-0.5">Master switch. When on, every admin event below also emails enabled recipients. Investor mail is unaffected.</p>
            </div>
          </label>
        </div>
      </div>

      {/* Per-event toggles */}
      <div className={`space-y-3 ${(settings.telegram_enabled || settings.whatsapp_enabled || settings.email_enabled) ? '' : 'opacity-50 pointer-events-none'}`}>
        <p className="text-white/60 text-xs uppercase tracking-widest font-semibold">Trigger Events</p>
        {EVENTS.map(ev => (
          <label
            key={ev.key}
            className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3 cursor-pointer hover:border-white/20 transition-colors"
            data-testid={`event-toggle-${ev.key}`}
          >
            <input
              type="checkbox"
              checked={!!settings.events[ev.key]}
              onChange={e => setEvent(ev.key, e.target.checked)}
              className="mt-1 accent-[#D4AF37] w-4 h-4"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                {ev.icon}
                <p className="text-white font-semibold text-sm">{ev.label}</p>
              </div>
              <p className="text-white/50 text-xs">{ev.desc}</p>
            </div>
          </label>
        ))}
      </div>

      {/* Drip Nudge Scheduler */}
      {drip && (
        <div
          data-testid="drip-nudge-section"
          className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Flame className="w-4 h-4 text-[#D4AF37]" /> Stale-Brief Drip Nudge
              </h3>
              <p className="text-white/50 text-xs mt-1">
                When a bespoke brief is in an active stage with no admin contact for
                <strong className="text-white/80"> {drip.threshold_hours}h+</strong>,
                push an admin reminder. Loop runs every <strong className="text-white/80">{drip.interval_hours}h</strong>;
                cools down <strong className="text-white/80">{drip.cooldown_hours}h</strong> per brief.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="drip-enabled-toggle">
              <input
                type="checkbox"
                checked={!!drip.enabled}
                disabled={dripSaving}
                onChange={(e) => saveDrip({ enabled: e.target.checked })}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className={`text-xs font-semibold ${drip.enabled ? 'text-[#D4AF37]' : 'text-white/40'}`}>
                {drip.enabled ? 'ON' : 'OFF'}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { key: 'threshold_hours', label: 'Silence threshold (h)', min: 1, max: 720 },
              { key: 'interval_hours',  label: 'Loop interval (h)',     min: 1, max: 168 },
              { key: 'cooldown_hours',  label: 'Cooldown / brief (h)',  min: 1, max: 720 },
            ].map(f => (
              <label key={f.key} className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 block mb-1">{f.label}</span>
                <input
                  type="number"
                  min={f.min}
                  max={f.max}
                  defaultValue={drip[f.key]}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v) && v !== drip[f.key]) saveDrip({ [f.key]: v });
                  }}
                  data-testid={`drip-input-${f.key}`}
                  className="w-full bg-black/40 border border-white/10 text-white text-sm rounded-md px-2.5 py-1.5 focus:outline-none focus:border-[#D4AF37]/60"
                />
              </label>
            ))}
          </div>

          {drip.last_run && (
            <div className="text-[11px] text-white/50 bg-black/30 rounded-md px-3 py-2" data-testid="drip-last-run">
              Last run: <span className="text-white/80">
                {drip.last_run.completed_at ? new Date(drip.last_run.completed_at).toLocaleString('en-GB') : '—'}
              </span>
              {' · '}eligible <span className="text-white/80">{drip.last_run.eligible}</span>
              {' · '}sent <span className="text-[#D4AF37]">{drip.last_run.sent}</span>
              {drip.last_run.ran === false && <span className="text-white/40"> (disabled)</span>}
            </div>
          )}

          <div className="pt-1">
            <Button
              onClick={runDripNow}
              disabled={dripRunning || !drip.enabled}
              variant="outline"
              className="border-white/20 text-white/80 hover:bg-white/5 hover:text-white"
              data-testid="drip-run-now-btn"
            >
              <Flame className="w-4 h-4 mr-2" /> {dripRunning ? 'Running…' : 'Run Drip Sweep Now'}
            </Button>
            {!drip.enabled && (
              <span className="text-[11px] text-white/40 ml-3">Enable above to send nudges.</span>
            )}
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex flex-col sm:flex-row gap-2.5 sm:justify-end pt-2">
        {hasAnyRecipient && (
          <>
            <Button
              onClick={triggerDigest}
              variant="outline"
              className="border-white/20 text-white/80 hover:bg-white/5 hover:text-white"
              data-testid="trigger-digest-btn"
            >
              <Flame className="w-4 h-4 mr-2" /> Run Hot-Lead Digest Now
            </Button>
            <Button
              onClick={() => sendTest(null)}
              disabled={testing === 'all'}
              variant="outline"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="send-test-btn"
            >
              <Send className="w-4 h-4 mr-2" /> {testing === 'all' ? 'Sending…' : 'Test All Recipients'}
            </Button>
          </>
        )}
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
          data-testid="notifications-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
